import { useEffect, useState } from 'react'
import { Plus, X, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Badge } from '../../lib/shadcn/badge'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetProductOptions,
  useAddProductOption,
  useRemoveProductOption,
} from '../../hooks/backend/settingsPage'

type OptionData = { categories: string[]; tags: string[]; wholesaleTiers?: string[] }

function OptionEditor({
  kind,
  label,
  noun,
  items,
  onChanged,
}: {
  kind: 'category' | 'tag' | 'wholesale_tier'
  label: string
  noun: string
  items: string[]
  onChanged: () => void
}) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const addOption = useAddProductOption()
  const removeOption = useRemoveProductOption()

  const add = async () => {
    const value = draft.trim()
    if (!value) return
    if (items.some(i => i.toLowerCase() === value.toLowerCase())) {
      toast.error(`"${value}" already exists`)
      return
    }
    try {
      setBusy('add')
      await addOption.trigger({ kind, value })
      setDraft('')
      onChanged()
    } catch {
      toast.error('Failed to add')
    } finally {
      setBusy(null)
    }
  }

  const remove = async (value: string) => {
    try {
      setBusy(value)
      await removeOption.trigger({ kind, value })
      onChanged()
    } catch {
      toast.error('Failed to remove')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void add()
            }
          }}
          placeholder={`Add a ${noun}…`}
          className="h-9"
        />
        <Button
          type="button"
          onClick={() => void add()}
          disabled={busy === 'add' || !draft.trim()}
          className="gap-1.5 shrink-0"
        >
          {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        {items.length === 0 ? (
          <span className="text-sm text-muted-foreground">No {noun} values yet.</span>
        ) : (
          items.map(item => (
            <Badge key={item} variant="secondary" className="gap-1 pr-1">
              {item}
              <button
                type="button"
                aria-label={`Remove ${item}`}
                disabled={busy === item}
                onClick={() => void remove(item)}
                className="rounded-sm hover:bg-muted-foreground/20 disabled:opacity-50"
              >
                {busy === item ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </button>
            </Badge>
          ))
        )}
      </div>
    </div>
  )
}

export default function ProductOptionsSettings() {
  const getOptions = useGetProductOptions()
  const options = (getOptions.data as OptionData | undefined) ?? { categories: [], tags: [] }

  useEffect(() => {
    void getOptions.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => {
    void getOptions.trigger({}, { skipCache: true })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Categories, Tags &amp; Wholesale Tiers</CardTitle>
        <CardDescription>
          Manage the values available in the Categories, Tags and Wholesale Tier fields on the product form.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <OptionEditor
          kind="category"
          label="Categories"
          noun="category"
          items={options.categories}
          onChanged={refresh}
        />
        <OptionEditor
          kind="tag"
          label="Tags"
          noun="tag"
          items={options.tags}
          onChanged={refresh}
        />
        <OptionEditor
          kind="wholesale_tier"
          label="Wholesale Tiers"
          noun="wholesale tier"
          items={options.wholesaleTiers ?? []}
          onChanged={refresh}
        />
      </CardContent>
    </Card>
  )
}
